type SlackNotifyContext = Record<string, unknown>;

const getEnv = (...keys: string[]): string | undefined => {
  for (const key of keys) {
    const val = process.env[key];
    if (val) return val;
  }
};

// Reports go to the backend, which posts them to Slack; the webhook URL never ships to the browser.
const API_BASE_URL = getEnv("REACT_APP_API_BASE_URL");
const ERROR_REPORT_URL = API_BASE_URL
  ? `${API_BASE_URL.trim().replace(/\/+$/, "")}/slack/client-error`
  : undefined;
const API_SECRET_KEY = getEnv("REACT_APP_API_SECRET_KEY");

const ENABLED = ["true", "1", "yes"].includes(
  (
    getEnv(
      "VITE_ENABLE_SLACK_ERROR_NOTIFICATIONS",
      "REACT_APP_ENABLE_SLACK_ERROR_NOTIFICATIONS"
    ) || ""
  ).toLowerCase()
);

// 🔥 Only allow HIGH VALUE alerts
const ALLOWED_SOURCES = ["window.error", "unhandledrejection", "api.handled" ,"networkerror"];

const normalizeError = (error: unknown) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack || "",
    };
  }

  return {
    name: "UnknownError",
    message: typeof error === "string" ? error : JSON.stringify(error),
    stack: "",
  };
};

const shouldIgnoreSlackError = (error: unknown, context?: SlackNotifyContext): boolean => {
  const normalized = normalizeError(error);
  const message = `${normalized.name} ${normalized.message}`.toLowerCase();
  const contextText = JSON.stringify(context || {}).toLowerCase();
  const combined = `${message} ${contextText}`;

  // Ignore noisy deploy/runtime asset loading failures.
  const ignorePatterns = [
    "chunkloaderror",
    "loading chunk",
    "failed to fetch dynamically imported module",
    "importing a module script failed",
    "unexpected token '<'",
    "loading css chunk",
    "loading hot update chunk",
    "loading hot update",
    "networkerror when attempting to fetch resource",
    "the script has an unsupported mime type",
    "failed to load module script",
    "resource size",
    "asset size limit",
    "large asset",
  ];

  return ignorePatterns.some((pattern) => combined.includes(pattern));
};

const getUser = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return {};
    const parsed = JSON.parse(raw);

    // We only send UUID for user identification in alerts
    // to avoid exposing email or internal numeric IDs.
    const uuid =
      parsed?.uuid ||
      parsed?.user?.uuid ||
      parsed?.data?.user?.uuid;

    return {
      uuid,
    };
  } catch {
    return {};
  }
};

const formatContext = (ctx?: SlackNotifyContext) => {
  if (!ctx) return "-";
  return Object.entries(ctx)
    .map(([k, v]) => `• ${k}: ${JSON.stringify(v)}`)
    .join("\n");
};

export const notifySlack = async (
  error: unknown,
  context?: SlackNotifyContext,
  source = "unknown"
) => {
  if (!ENABLED || !ERROR_REPORT_URL) return;

  // 🚫 Filter noise
  if (!ALLOWED_SOURCES.includes(source)) return;
  if (shouldIgnoreSlackError(error, context)) return;

  const { name, message, stack } = normalizeError(error);
  const { uuid } = getUser();

  const contextText = context && Object.keys(context).length ? formatContext(context) : "";
  const summary = `${name}: ${message}`;

  const report = {
    app: "admin",
    kind: source === "api.handled" || source === "networkerror" ? "api" : "runtime",
    source,
    summary: summary.slice(0, 1000),
    stack: stack ? stack.slice(0, 800) : undefined,
    page_url: window.location.pathname.slice(0, 1000),
    user_id: uuid ? String(uuid).slice(0, 64) : undefined,
    user_agent: navigator.userAgent.slice(0, 500),
    details: contextText ? contextText.slice(0, 3000) : undefined,
  };

  try {
    const response = await fetch(ERROR_REPORT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(API_SECRET_KEY ? { secretkey: API_SECRET_KEY } : {}),
      },
      body: JSON.stringify(report),
      keepalive: true,
      credentials: "omit",
    });

    if (!response.ok) {
      console.error("[Slack Notify Error]", {
        status: response.status,
        statusText: response.statusText,
      });
    }
  } catch {
    console.error("[Slack Notify Error] Failed to send alert");
  }
};

// 🔥 INIT (clean version)
export const initSlackTracking = () => {
  window.addEventListener("error", (event) => {
    notifySlack(event.error || event.message, undefined, "window.error");
  });

  window.addEventListener("unhandledrejection", (event) => {
    notifySlack(event.reason, {}, "unhandledrejection");
  });

  // ❌ REMOVE console.error tracking (too noisy)
};